import { useCallback, useRef, useState } from 'react';
import api from '@helpers/api';
import {
    ACTION_TYPES_BY_PLATFORM,
    DEFAULT_ACTIONS_BY_PLATFORM,
    WEBHOOK_METHODS,
    getDefaultConfigForType,
    newActionKey,
} from '../enums/actionTypes';

const EMPTY_INTEGRATION = {
    id: null,
    plan_id: '',
    event: 'payments.confirmed',
    platform: '',
    name: '',
    active: false,
    apiKey: '',
    endpoint: '',
};

function buildActionsFromRaw(rawActions = []) {
    return rawActions.map(a => ({
        _key: newActionKey(),
        id: a.id,
        type: a.type || a.action_type,
        config: a.config || a.action_config || {},
    }));
}

// Mirrors the backend webhook_call rules (HasWebhookActionRules) so the user
// gets a friendly message before hitting a 422. Masked secrets ("***xxxx")
// count as filled — echoing them back tells the backend to keep the stored value.
function validateWebhookConfig(config, position) {
    const c = config ?? {};
    const filled = v => typeof v === 'string' && v.trim().length > 0;

    const url = (c.url ?? '').trim();
    if (!url) return `A URL do webhook na ação ${position} é obrigatória.`;
    if (!/^https:\/\//i.test(url)) return `A URL do webhook na ação ${position} deve começar com https://.`;
    try { new URL(url); } catch { return `A URL do webhook na ação ${position} é inválida.`; }

    if (!WEBHOOK_METHODS.includes(c.method)) {
        return `Selecione um método HTTP válido na ação ${position}.`;
    }

    const auth = c.auth ?? {};
    if (auth.type === 'bearer' && !filled(auth.token)) {
        return `Informe o token de autenticação na ação ${position}.`;
    }
    if (auth.type === 'basic' && (!filled(auth.username) || !filled(auth.password))) {
        return `Informe usuário e senha na ação ${position}.`;
    }
    if (auth.type === 'api_key' && (!filled(auth.header_name) || !filled(auth.value))) {
        return `Informe o nome do cabeçalho e a chave na ação ${position}.`;
    }

    const headerKeyPattern = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
    for (const h of c.headers ?? []) {
        if (!filled(h?.key) || !filled(h?.value)) {
            return `Preencha nome e valor de todos os cabeçalhos na ação ${position}.`;
        }
        if (!headerKeyPattern.test(h.key.trim())) {
            return `O cabeçalho "${h.key}" na ação ${position} contém caracteres inválidos.`;
        }
        if (['host', 'content-length'].includes(h.key.trim().toLowerCase())) {
            return `O cabeçalho "${h.key}" é controlado automaticamente e não pode ser definido.`;
        }
    }

    const targets = new Set();
    for (const m of c.field_mappings ?? []) {
        if (!filled(m?.source)) {
            return `Selecione o dado do comprador em todos os mapeamentos da ação ${position}.`;
        }
        if (!filled(m?.target)) {
            return `Informe o nome do campo de destino em todos os mapeamentos da ação ${position}.`;
        }
        const target = m.target.trim();
        if (targets.has(target)) {
            return `O campo de destino "${target}" está duplicado na ação ${position}.`;
        }
        targets.add(target);
    }

    if (typeof c.body_template === 'string' && c.body_template.trim()) {
        try {
            const parsed = JSON.parse(c.body_template);
            if (parsed === null || typeof parsed !== 'object') throw new Error('not an object');
        } catch {
            return `O template do corpo na ação ${position} deve ser um JSON válido (objeto ou lista).`;
        }
    }

    return null;
}

function buildDefaultActions(platform) {
    return (DEFAULT_ACTIONS_BY_PLATFORM[platform] ?? []).map(a => ({
        _key: newActionKey(),
        id: null,
        type: a.type,
        config: { ...a.config },
    }));
}

export default function useManageIntegration({ communityRef }) {
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('create'); // 'create' | 'edit'
    const [step, setStep] = useState(0); // 0 = integration form, 1 = actions (create only)
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [integration, setIntegration] = useState({ ...EMPTY_INTEGRATION });
    const [actions, setActions] = useState([]);
    const [removedActionIds, setRemovedActionIds] = useState([]);
    // Per-action test-fire state, keyed by action._key: { loading?, result?, error? }
    const [testStates, setTestStates] = useState({});

    // Integration created in step 1 (create flow) – need to track for cancel/rollback
    const createdIntegrationId = useRef(null);
    const callbackRef = useRef(() => { });

    // ─── Expose imperative API ───────────────────────────────────────────────

    const openCreate = useCallback((callback = () => { }) => {
        callbackRef.current = callback;
        createdIntegrationId.current = null;
        setIntegration({ ...EMPTY_INTEGRATION });
        setActions([]);
        setRemovedActionIds([]);
        setTestStates({});
        setStep(0);
        setError(null);
        setMode('create');
        setOpen(true);
    }, []);

    const openEdit = useCallback(async (integrationSummary, callback = () => { }) => {
        callbackRef.current = callback;
        createdIntegrationId.current = null;
        setError(null);
        setMode('edit');
        setStep(0);
        setLoading(true);
        setOpen(true);

        try {
            const res = await api.get(`integration/${integrationSummary.id}`);
            const data = res?.data ?? res;
            setIntegration({
                id: data.id,
                plan_id: data.plan ?? data.plan_id ?? '',
                event: data.event ?? '',
                platform: data.platform ?? '',
                name: data.name ?? '',
                active: data.active ?? true,
                apiKey: data.config?.apiKey ?? '',
                endpoint: data.config?.endpoint ?? '',
            });
            setActions(buildActionsFromRaw(data.actions ?? []));
            setRemovedActionIds([]);
            setTestStates({});
        } catch (e) {
            console.error(e);
            setError('Não foi possível carregar a integração.');
        } finally {
            setLoading(false);
        }
    }, []);

    // ─── Field helpers ───────────────────────────────────────────────────────

    const updateIntegration = useCallback((patch) => {
        setIntegration(prev => ({ ...prev, ...patch }));
    }, []);

    // ─── Validation ──────────────────────────────────────────────────────────

    function validateStep0() {
        if (!integration.platform) return 'Selecione a plataforma.';
        if (!integration.plan_id) return 'Selecione um plano.';
        if (!integration.event) return 'Selecione o evento.';
        if (!integration.name || integration.name.trim().length < 3) return 'O nome deve ter ao menos 3 caracteres.';
        if (integration.name.trim().length > 50) return 'O nome pode ter no máximo 50 caracteres.';
        if (integration.platform === 'activecampaign') {
            if (!integration.apiKey?.trim()) return 'A chave de API do ActiveCampaign é obrigatória.';
            if (!integration.endpoint?.trim()) return 'O endpoint do ActiveCampaign é obrigatório.';
        }
        return null;
    }

    function validateActions() {
        for (let i = 0; i < actions.length; i++) {
            const a = actions[i];
            if (a.type === 'webhook_call') {
                if (integration.platform === 'webhook') {
                    const err = validateWebhookConfig(a.config, i + 1);
                    if (err) return err;
                } else if (!a.config?.webhook_url?.trim()) {
                    return `A URL do webhook na ação ${i + 1} é obrigatória.`;
                }
            }
            if (a.type === 'tag_add' && !a.config?.tag_id) {
                return `Selecione uma tag na ação ${i + 1}.`;
            }
            if (a.type === 'list_add' && !a.config?.list_id) {
                return `Selecione uma lista na ação ${i + 1}.`;
            }
        }
        if (actions.length === 0) return 'Adicione ao menos uma ação.';
        return null;
    }

    // ─── Create flow ─────────────────────────────────────────────────────────

    async function handleCreateStep0() {
        const err = validateStep0();
        if (err) { setError(err); return; }

        setError(null);
        setSubmitting(true);
        try {
            const community = communityRef.current;
            const payload = {
                community: community?.id,
                plan: integration.plan_id || null,
                event: integration.event,
                platform: integration.platform,
                name: integration.name.trim(),
                active: false, // will be activated after actions are saved
                ...(integration.platform === 'activecampaign' && {
                    config: {
                        apiKey: integration.apiKey.trim(),
                        endpoint: integration.endpoint.trim(),
                    },
                }),
            };
            const res = await api.post('integration', payload);
            const data = res?.data ?? res;
            if (res?.response?.status >= 400 || !data?.id) {
                setError(data?.message || 'Erro ao criar integração.');
                return;
            }
            createdIntegrationId.current = data.id;
            setIntegration(prev => ({ ...prev, id: data.id }));

            // Pre-populate default actions for the chosen platform
            setActions(buildDefaultActions(integration.platform));
            setStep(1);
        } catch (e) {
            console.error(e);
            setError('Erro ao criar integração. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    }

    async function handleCreateStep1() {
        const err = validateActions();
        if (err) { setError(err); return; }

        setError(null);
        setSubmitting(true);
        try {
            const integrationId = createdIntegrationId.current;
            for (let i = 0; i < actions.length; i++) {
                const a = actions[i];
                await api.post(`integration/${integrationId}/actions`, {
                    type: a.type,
                    config: a.config,
                    priority: i + 1,
                });
            }
            window.setSnackbar('Integração criada com sucesso');
            setOpen(false);
            callbackRef.current();
        } catch (e) {
            console.error(e);
            setError('Erro ao salvar as ações. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    }

    // ─── Edit flow ───────────────────────────────────────────────────────────

    async function handleEdit() {
        const err0 = validateStep0();
        if (err0) { setError(err0); return; }
        const errA = validateActions();
        if (errA) { setError(errA); return; }

        setError(null);
        setSubmitting(true);
        try {
            const integrationId = integration.id;

            // 1. Update integration name + active
            await api.patch(`integration/${integrationId}`, {
                name: integration.name.trim(),
                active: integration.active,
            });

            // 2. Delete removed actions
            for (const actionId of removedActionIds) {
                await api.delete(`integration/${integrationId}/actions/${actionId}`);
            }

            // 3. Update or create actions
            for (let i = 0; i < actions.length; i++) {
                const a = actions[i];
                if (a.id) {
                    await api.patch(`integration/${integrationId}/actions/${a.id}`, {
                        type: a.type,
                        config: a.config,
                        priority: i + 1,
                    });
                } else {
                    await api.post(`integration/${integrationId}/actions`, {
                        type: a.type,
                        config: a.config,
                        priority: i + 1,
                    });
                }
            }

            window.setSnackbar('Integração atualizada com sucesso');
            setOpen(false);
            callbackRef.current();
        } catch (e) {
            console.error(e);
            setError('Erro ao salvar. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    }

    // ─── Actions management ──────────────────────────────────────────────────

    function handleAddAction() {
        const platform = integration.platform;
        const availableTypes = ACTION_TYPES_BY_PLATFORM[platform] ?? [];
        const defaultType = availableTypes[0]?.value ?? 'webhook_call';
        setActions(prev => [
            ...prev,
            { _key: newActionKey(), id: null, type: defaultType, config: getDefaultConfigForType(defaultType, platform) },
        ]);
    }

    function handleUpdateAction(key, patch) {
        setActions(prev =>
            prev.map(a => a._key === key ? { ...a, ...patch } : a)
        );
        // A config change invalidates any previous test-fire result
        setTestStates(prev => (prev[key] ? { ...prev, [key]: null } : prev));
    }

    function handleRemoveAction(key) {
        setActions(prev => {
            const removed = prev.find(a => a._key === key);
            if (removed?.id) {
                setRemovedActionIds(ids => [...ids, removed.id]);
            }
            return prev.filter(a => a._key !== key);
        });
        setTestStates(prev => (prev[key] ? { ...prev, [key]: null } : prev));
    }

    // ─── Test fire (generic webhook) ─────────────────────────────────────────

    async function handleTestAction(key) {
        const index = actions.findIndex(a => a._key === key);
        if (index === -1) return;

        const action = actions[index];
        const integrationId = integration.id ?? createdIntegrationId.current;
        if (!integrationId) return;

        const validationError = validateWebhookConfig(action.config, index + 1);
        if (validationError) {
            setTestStates(prev => ({ ...prev, [key]: { error: validationError } }));
            return;
        }

        setTestStates(prev => ({ ...prev, [key]: { loading: true } }));
        try {
            // Config always goes inline so the test fires what is on screen,
            // saved or not; the backend swaps masked secrets for stored ones.
            const url = action.id
                ? `integration/${integrationId}/actions/${action.id}/test`
                : `integration/${integrationId}/actions/test`;
            const res = await api.post(url, { config: action.config });
            const data = res?.data ?? res;

            if (res?.response?.status >= 400 || typeof data?.response_code !== 'number') {
                const message = res?.response?.data?.message
                    || data?.message
                    || 'Não foi possível testar o webhook.';
                setTestStates(prev => ({ ...prev, [key]: { error: message } }));
                return;
            }

            setTestStates(prev => ({
                ...prev,
                [key]: {
                    result: {
                        response_code: data.response_code,
                        response_body: data.response_body ?? '',
                    },
                },
            }));
        } catch (e) {
            console.error(e);
            setTestStates(prev => ({ ...prev, [key]: { error: 'Não foi possível testar o webhook.' } }));
        }
    }

    async function handleToggle() {
        if (!integration.id) return;

        const previousActive = !!integration.active;

        // Optimistically update
        setIntegration(prev => ({ ...prev, active: !previousActive }));

        try {
            const res = await api.patch(`integration/${integration.id}/toggle`);
            if (res?.response?.status >= 400) {
                throw new Error(res?.response?.data?.message || 'Erro ao alternar status.');
            }
            window.setSnackbar(`Integração ${!previousActive ? 'ativada' : 'desativada'} com sucesso`);
        } catch (e) {
            console.error(e);
            window.setSnackbar(e.message || 'Não foi possível alterar o status da integração');
            // Revert
            setIntegration(prev => ({ ...prev, active: previousActive }));
        }
    }

    // ─── Modal control ───────────────────────────────────────────────────────

    async function handleClose() {
        // If we're on step 1 of creation (integration was already created), delete it
        if (mode === 'create' && step === 1 && createdIntegrationId.current) {
            try {
                await api.delete(`integration/${createdIntegrationId.current}`);
            } catch (e) {
                console.error('Failed to rollback integration on cancel', e);
            }
            createdIntegrationId.current = null;
        }
        setOpen(false);
        setError(null);
        callbackRef.current();
    }

    function handleBack() {
        if (mode === 'create' && step === 1) {
            setStep(0);
            setError(null);
        }
    }

    async function handleSubmit() {
        if (mode === 'create') {
            if (step === 0) await handleCreateStep0();
            else await handleCreateStep1();
        } else {
            await handleEdit();
        }
    }

    function handleSecondary() {
        if (mode === 'create' && step === 1) {
            handleBack();
        } else {
            handleClose();
        }
    }

    // ─── Derived ─────────────────────────────────────────────────────────────

    const isCreate = mode === 'create';
    const isStep0 = step === 0;
    const submitLabel = isCreate
        ? (isStep0 ? 'Salvar Integração' : 'Salvar ações')
        : 'Salvar integração';
    const cancelLabel = isCreate && step === 1 ? 'Voltar' : 'Cancelar';
    const title = isCreate ? 'Nova Integração' : 'Editar Integração';

    return {
        // modal state
        open,
        loading,
        submitting,
        error,
        // data
        mode,
        step,
        integration,
        actions,
        testStates,
        // handlers
        openCreate,
        openEdit,
        updateIntegration,
        handleAddAction,
        handleUpdateAction,
        handleRemoveAction,
        handleTestAction,
        handleToggle,
        handleSubmit,
        handleSecondary,
        handleClose,
        // ui
        submitLabel,
        cancelLabel,
        title,
        community: communityRef,
    };
}
